import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, PlusCircle, RefreshCw, Filter, X, CalendarPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { api, fileUrl } from '../../lib/api';
import { SectionHeader, EmptyState, formatDate } from '../../components/Shared';
import { EventCard } from '../../components/election/EventCard';
import { ReminderSignup } from '../../components/election/ReminderSignup';

const RACE_OPTIONS = [
  { value: 'all', label: 'All races' },
  { value: 'mayor', label: 'Mayor' },
  { value: 'council', label: 'Ward Councillor' },
  { value: 'trustee', label: 'School Board Trustee' },
  { value: 'general', label: 'General (CKL-wide)' },
];
const WHEN_OPTIONS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'all', label: 'All' },
];

const fmtStamp = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Toronto' }) + ' ET'; } catch { return formatDate(iso); }
};

// Full CKL election calendar: Mayor, Ward 1-8, School Board Trustee and
// general election events. Not limited to Ward 8. Filters live in the URL
// so a filtered view can be shared.
export default function ElectionEvents() {
  const [params, setParams] = useSearchParams();
  const race = params.get('race') || 'all';
  const ward = params.get('ward') || 'all';
  const type = params.get('type') || 'all';
  const when = params.get('when') || 'upcoming';
  const [q, setQ] = useState(params.get('q') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === 'all' || (k === 'when' && v === 'upcoming')) next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  const load = () => {
    setLoading(true); setError(false);
    api.get('/election/events', { params: { race, ward, event_type: type, when: 'all' } })
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(load, [race, ward, type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (window.location.hash.startsWith('#event-') && data) {
      const el = document.querySelector(`[data-event-id="${window.location.hash.slice(7)}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [data]);

  const needle = q.trim().toLowerCase();
  const match = (ev) => !needle || `${ev.title} ${ev.organizer} ${ev.venue_name} ${ev.address} ${(ev.candidates || []).join(' ')} ${ev.description}`.toLowerCase().includes(needle);
  const upcoming = useMemo(() => (data?.upcoming || []).filter(match), [data, needle]); // eslint-disable-line react-hooks/exhaustive-deps
  const past = useMemo(() => (data?.past || []).filter(match), [data, needle]); // eslint-disable-line react-hooks/exhaustive-deps
  const types = data?.event_types || {};
  const anyFilter = race !== 'all' || ward !== 'all' || type !== 'all' || needle;
  const clearAll = () => { setQ(''); setParams(new URLSearchParams(), { replace: true }); };

  return (
    <div data-testid="election-events-page">
      <SectionHeader kicker="ALL OF KAWARTHA LAKES" title="Debates and candidate events" dek="Every public election event we know about: Mayor, Ward 1 to 8 Councillor, School Board Trustee and city-wide events. Meet and greets, coffee with a candidate, debates, forums, BBQs and more. Times are Eastern." />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="events-submit-btn"><Link to="/elections/submit-event"><PlusCircle className="h-4 w-4 mr-1.5" /> Submit an Event</Link></Button>
        <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press bg-white" data-testid="events-subscribe-calendar-btn">
          <a href={fileUrl(`/api/election/events/calendar.ics${race !== 'all' || ward !== 'all' ? `?${new URLSearchParams({ ...(race !== 'all' ? { race } : {}), ...(ward !== 'all' ? { ward } : {}) })}` : ''}`)} download title="Download every upcoming event as one calendar file"><CalendarPlus className="h-4 w-4 mr-1.5" /> Save all upcoming to calendar</a>
        </Button>
        {data?.last_updated && <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="events-last-checked"><RefreshCw className="h-3 w-3" /> Sources last checked {fmtStamp(data.last_updated)}</span>}
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-6" data-testid="events-filters">
        <p className="text-xs font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5 mb-3"><Filter className="h-3.5 w-3.5" /> Filter events</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600" htmlFor="events-race">Race</label>
            <Select value={race} onValueChange={(v) => setParam('race', v)}>
              <SelectTrigger id="events-race" className="h-11 bg-white mt-1" data-testid="events-filter-race"><SelectValue /></SelectTrigger>
              <SelectContent>{RACE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`events-race-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600" htmlFor="events-ward">Ward</label>
            <Select value={ward} onValueChange={(v) => setParam('ward', v)}>
              <SelectTrigger id="events-ward" className="h-11 bg-white mt-1" data-testid="events-filter-ward"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All wards</SelectItem>
                <SelectItem value="citywide">City-wide only</SelectItem>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)} data-testid={`events-ward-${n}`}>Ward {n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600" htmlFor="events-type">Event type</label>
            <Select value={type} onValueChange={(v) => setParam('type', v)}>
              <SelectTrigger id="events-type" className="h-11 bg-white mt-1" data-testid="events-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(types).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`events-type-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600" htmlFor="events-when">When</label>
            <Select value={when} onValueChange={(v) => setParam('when', v)}>
              <SelectTrigger id="events-when" className="h-11 bg-white mt-1" data-testid="events-filter-when"><SelectValue /></SelectTrigger>
              <SelectContent>{WHEN_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`events-when-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600" htmlFor="events-search">Search</label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input id="events-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Candidate, venue, organizer" className="h-11 pl-9 bg-white" data-testid="events-filter-search" />
            </div>
          </div>
        </div>
        {anyFilter && (
          <button type="button" onClick={clearAll} className="mt-3 inline-flex items-center text-sm font-semibold text-[#24406f] underline underline-offset-2" data-testid="events-clear-filters"><X className="h-3.5 w-3.5 mr-1" /> Clear filters</button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="events-loading">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-36 w-full rounded-2xl" />)}</div>
      ) : error ? (
        <div className="text-center py-10" data-testid="events-error">
          <p className="font-bold text-[#1a2744]">The event list could not be loaded.</p>
          <Button onClick={load} variant="outline" className="mt-3 h-11 border-[#1a2744] text-[#1a2744] font-bold" data-testid="events-retry">Try again</Button>
        </div>
      ) : (
        <div className="space-y-10">
          {(when === 'upcoming' || when === 'all') && (
            <section data-testid="events-upcoming-section">
              <h3 className="text-xl font-bold text-[#1a2744] mb-3">Upcoming <span className="text-slate-400 font-semibold">({upcoming.length})</span></h3>
              {upcoming.length === 0 ? (
                <EmptyState title={anyFilter ? 'No upcoming events match these filters' : 'No upcoming events listed yet'} subtitle="Know of one? Use Submit an Event and it will be reviewed before it appears here." />
              ) : (
                <div className="space-y-3">{upcoming.map((ev) => <EventCard key={ev.id} ev={ev} />)}</div>
              )}
            </section>
          )}
          {(when === 'past' || when === 'all') && (
            <section data-testid="events-past-section">
              <h3 className="text-xl font-bold text-[#1a2744] mb-3">Past events <span className="text-slate-400 font-semibold">({past.length})</span></h3>
              {past.length === 0 ? (
                <EmptyState title="No past events to show" subtitle="Past events stay listed here with any recordings that are shared afterwards." />
              ) : (
                <div className="space-y-3">{past.map((ev) => <EventCard key={ev.id} ev={ev} />)}</div>
              )}
            </section>
          )}
          {when === 'upcoming' && past.length > 0 && (
            <button type="button" onClick={() => setParam('when', 'past')} className="text-sm font-bold text-[#1a2744] underline underline-offset-2" data-testid="events-show-past">Show {past.length} past event{past.length === 1 ? '' : 's'}</button>
          )}
        </div>
      )}

      <div className="mt-10"><ReminderSignup testId="events-reminder-signup" /></div>

      <p className="mt-10 text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="events-disclaimer">
        Events are listed from organizer announcements, official pages and resident submissions, and each shows its source where one exists. Details can change. Confirm with the organizer before you travel. Cancellations are shown, not removed.
      </p>
    </div>
  );
}
